// frontend/src/widgets/OpportunityInboxWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-09 09:06 BST
//
// Description:
//   Opportunities Inbox widget (left column, top).
//   Shows flagged items requiring attention. Clicking selects an opportunity.
//
// Mobile Responsiveness (2026-09-09):
//   • Preserves the original clickable row div and onSelect(it.id) behaviour.
//   • Preserves the original navigation divs and tooltip implementation.
//   • Uses natural list height on mobile and retains internal scrolling on desktop.
//   • Allows opportunity name, stage and flag text to wrap on mobile.
//   • Preserves the existing desktop ellipsis treatment.
//   • Keeps opportunity card text on the normal theme primary/text colour.
//   • No API, filtering, selection or modal behaviour is changed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";

// -------------------- Gold-standard Tooltip --------------------
function useGoldTooltip(theme) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const rafRef = useRef(null);
  const openRef = useRef(false);

  const bg = theme?.primary_color || "#111827";

  const hide = () => {
    openRef.current = false;
    setOpen(false);
    setText("");
  };

  const show = (t) => {
    const s = String(t || "").trim();
    if (!s) return;
    openRef.current = true;
    setText(s);
    setOpen(true);
  };

  const move = (clientX, clientY) => {
    if (!openRef.current) return;

    const pad = 12;
    const maxW = 360;
    const vw = window.innerWidth || 1200;
    const vh = window.innerHeight || 800;

    let x = clientX + 14;
    let y = clientY + 14;

    const clampX = vw - maxW - pad;
    if (x > clampX) x = Math.max(pad, vw - maxW - pad);

    const approxH = 72;
    const clampY = vh - approxH - pad;
    if (y > clampY) y = Math.max(pad, vh - approxH - pad);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setPos({ x, y }));
  };

  useEffect(() => {
    const onScroll = () => hide();
    const onKey = (e) => e.key === "Escape" && hide();
    const onBlur = () => hide();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const Tooltip = useMemo(() => {
    if (!open || !text) return null;

    return createPortal(
      <div
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          zIndex: 99999,
          maxWidth: 360,
          background: bg,
          color: "#fff",
          padding: "10px 12px",
          borderRadius: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1.25,
          whiteSpace: "pre-line",
          pointerEvents: "none",
        }}
      >
        {text}
      </div>,
      document.body
    );
  }, [open, text, pos, bg]);

  const bind = (tooltipText) => ({
    onMouseEnter: (e) => {
      show(tooltipText);
      move(e.clientX, e.clientY);
    },
    onMouseMove: (e) => move(e.clientX, e.clientY),
    onMouseLeave: hide,
  });

  return { Tooltip, bind };
}

// -------------------- Widget --------------------
export default function OpportunityInboxWidget({
  items = [],
  loading = false,
  error = "",
  selectedId = null,
  onSelect,
}) {
  const { theme } = useTheme();
  const { Tooltip, bind } = useGoldTooltip(theme);

  const [navItem, setNavItem] = useState("all");

  const navItems = [
    { id: "all", label: "All" },
    { id: "flagged", label: "Flagged" },
    { id: "urgent", label: "Urgent" },
  ];

  const activeIndex = navItems.findIndex((n) => n.id === navItem);

  const wrap = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    }),
    [theme]
  );

  const list = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingRight: 4,
    minHeight: 0,
  };

  return (
    <div
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={wrap}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", fontSize: 12, fontWeight: 800, opacity: 0.75 }}>
        {loading ? "Loading…" : `Items: ${items.length}`}
      </div>

      {/* Inbox navigation */}
      <div style={{ margin: "10px 0 12px" }}>
        <div style={{ fontSize: 11, fontWeight: 900, opacity: 0.6, marginBottom: 6 }}>
          Inbox navigation
        </div>

        <div
          style={{
            position: "relative",
            display: "flex",
            background: theme?.panel_bg || "#f4f4f5",
            padding: 6,
            borderRadius: 12,
          }}
        >
          {/* Sliding indicator */}
          <div
            style={{
              position: "absolute",
              top: 6,
              bottom: 6,
              left: `calc(${activeIndex} * (100% / ${navItems.length}))`,
              width: `calc(100% / ${navItems.length})`,
              background: theme?.accent_color || "#2563eb",
              borderRadius: 10,
              transition: "left 220ms cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
            }}
          />

          {navItems.map((nav) => (
            <div
              key={nav.id}
              onClick={() => setNavItem(nav.id)}
              style={{
                flex: 1,
                textAlign: "center",
                padding: "8px 10px",
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 900,
                cursor: "pointer",
                zIndex: 1,
                color: navItem === nav.id ? "#fff" : theme?.text_primary || "#111827",
                transition: "color 180ms ease",
              }}
            >
              {nav.label}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div
          style={{
            color: theme?.danger || "#b91c1c",
            fontSize: 12,
            fontWeight: 800,
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      )}

      <div
        className="overflow-visible md:flex-1 md:overflow-y-auto"
        style={list}
      >
        {items.map((it) => {
          const active = selectedId === it.id;
          const flags = Array.isArray(it.flags) ? it.flags.filter(Boolean) : [];
          const flagsShort =
            flags.length <= 2 ? flags.join(" · ") : `${flags.slice(0, 2).join(" · ")}…`;
          const flagsFull = flags.join("\n");

          return (
            <div
              key={it.id}
              onClick={() => onSelect && onSelect(it.id)}
              style={{
                borderRadius: 12,
                padding: 10,
                cursor: "pointer",
                color: theme?.primary_color || theme?.text_primary || "#111827",
                border: active
                  ? `2px solid ${theme?.accent_color || "#2563eb"}`
                  : "1px solid rgba(0,0,0,0.08)",
                background: active
                  ? theme?.accent_soft || "rgba(37,99,235,0.08)"
                  : theme?.panel_bg || "#fafafa",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:justify-between sm:gap-2.5">
                <div
                  className="min-w-0 break-words md:overflow-hidden md:text-ellipsis md:whitespace-nowrap"
                  style={{
                    fontWeight: 900,
                    fontSize: 13,
                    overflowWrap: "anywhere",
                  }}
                >
                  {it.name || `Opportunity ${it.id}`}
                </div>
                <div
                  className="min-w-0 break-words sm:shrink-0 sm:text-right md:max-w-[45%] md:overflow-hidden md:text-ellipsis md:whitespace-nowrap"
                  style={{
                    fontSize: 11,
                    opacity: 0.75,
                    fontWeight: 900,
                    overflowWrap: "anywhere",
                  }}
                >
                  {String(it.stage || "").toUpperCase()}
                </div>
              </div>

              <span
                className="min-w-0 break-words md:overflow-hidden md:text-ellipsis md:whitespace-nowrap"
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  fontWeight: 800,
                  overflowWrap: "anywhere",
                }}
                {...(flagsFull ? bind(flagsFull) : {})}
              >
                {flagsShort || "—"}
              </span>
            </div>
          );
        })}
      </div>

      {Tooltip}
    </div>
  );
}
